/* eslint-disable default-param-last */
// 数字处理 https://mikemcl.github.io/bignumber.js/#rounding-mode
import BN, { BigNumber } from 'bignumber.js';

BN.config({
  DECIMAL_PLACES: 20, // 小数位数
  EXPONENTIAL_AT: 20, // 科学标识法位数
});

// 处理返回结果
function handleReturn(result): any {
  const rs: any = result.toFixed ? result.toFixed() : result;

  return /[a-z]/i.test(rs) ? '0' : rs;
}

/**
 *
 * 生成随机数
 *
 * @param {number} num 随机小数位数
 *
 * random(3) // '0.105'
 * random(1) // '0.1'
 */
function random(num: number) {
  return handleReturn(BN.random(num));
}

/**
 * 累计和
 *
 * @param {...str} str 需要累加的字符串
 *
 * sum(['1', '2', '3']) // '6'
 * sum([null, '1']) // '0'
 * sum(['sdaf', '1']) // '0'
 */
function sum(str: string[]) {
  return handleReturn(BN.sum(...str));
}

/**
 * 绝对值
 *
 * @param {str} str 字符串
 *
 * abs('-112') // '112'
 * abs('dsaf') // '0'
 */
function abs(str: string) {
  return handleReturn(BN(`${str}`).abs());
}

/**
 * 加法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * plus('1', '9') // '10'
 * plus('dsaf', 1) // '0'
 */
function plus(s1, s2) {
  return handleReturn(BN(`${s1}`).plus(`${s2}`));
}

/**
 * 减法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * minus('10', '5') // '5'
 * minus('dsaf', 1) // '0'
 */
function minus(s1, s2) {
  return handleReturn(BN(`${s1}`).minus(`${s2}`));
}

/**
 * 乘法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * times('1', '9') // '9'
 * times('dsaf', 1) // '0'
 */
function times(s1, s2) {
  return handleReturn(BN(`${s1}`).times(`${s2}`));
}

/**
 * 除法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * div('8', '1') // '8'
 * div('10', '3') // '3.33333333333333333333333333333333333333333333333333333333333333333333333'
 * div('dsaf', 1) // '0'
 */
function div(s1, s2) {
  if (`${s2}` === '0') return '0';
  return handleReturn(BN(`${s1}`).div(`${s2}`));
}

/**
 * 整数除法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * idiv('10', '3') // '3'
 * idiv('dsaf', 1) // '0'
 */
function idiv(s1, s2) {
  if (`${s2}` === '0') return '0';
  return handleReturn(BN(`${s1}`).idiv(`${s2}`));
}

/**
 * 大于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * gt('4', '2') // true
 * gt('3', '3') // false
 * gt('dsaf', 1) // false
 */
function gt(s1, s2) {
  return BN(`${s1}`).gt(`${s2}`);
}

/**
 * 大于等于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * gte('10', '10') // true
 * gte('10', '3') // false
 * gte('dsaf', 1) // false
 */
function gte(s1, s2) {
  return BN(`${s1}`).gte(`${s2}`);
}

/**
 * 小于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * lt('2', '3') // true
 * lt('4', '2') // false
 * lt('dsaf', 1) // false
 */
function lt(s1, s2) {
  return BN(`${s1}`).lt(`${s2}`);
}

/**
 * 小于等于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * lte('2', '2') // true
 * lte('4', '2') // false
 * lte('dsaf', 1) // false
 */
function lte(s1, s2) {
  return BN(`${s1}`).lte(`${s2}`);
}

/**
 * 等于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * eq('10', '10') // true
 * eq('10', '3') // false
 * eq('dsaf', 1) // false
 */
function eq(s1, s2) {
  return BN(`${s1}`).eq(`${s2}`);
}

/**
 * 整体精度精度
 *
 * @param {string} s1 需要取精度的字符串
 * @param {number} d 几位精度
 * @param {string} rm 四舍五入方式 默认向上取证，1向下取整
 *
 * sd('125.125', 2) // '130'
 * sd('125.125', 2, 1) // '120'
 * sd('sdfsd', 2, 1) // '0'
 */
function sd(s1, d, rm) {
  if (!d) throw new Error('精度为必填');
  return handleReturn(BN(`${s1}`).sd(d, rm));
}

/**
 * 不带千分位保留几位小数 默认为向下取整
 *
 * @param {string} s1 需要格式化的字符串
 * @param {number} dp 保留几位小数 默认保留所有小数
 * @param {number} [rm=1] 最后一位保留方式，默认为1向下取整
 *
 * toFixed('123.555') // '123.555'
 * toFixed('123.555', 0) // '124'
 * toFixed('123.555', 1) // '123.5'
 * toFixed('123.555', 1, 1) // '123.5'
 * toFixed('123.555', 6) // '123.555000'
 * toFixed('111123.555', 6) // '111123.555000'
 */
function toFixed(s1: string, dp: number, rm = 1) {
  return handleReturn(BN(`${s1}`)['toFixed' as any](dp, rm));
}

/**
 * 带千分位保留几位小数 默认为向下取整
 *
 * @param {string} s1 需要格式化的字符串
 * @param {number} dp 保留几位小数 默认保留所有小数
 * @param {number} [rm=1] 最后一位保留方式，默认为1向下取整
 * @param {Object} format 格式化配置 参考 https://mikemcl.github.io/bignumber.js/#format
 *
 * toFormat('123456789.567') // '123,456,789.567'
 * toFormat('123456789.567', 1) // '123,456,789.5'
 * toFormat('123456789.567', 5) // '123,456,789.56700'
 * toFormat('123456789.567', 1, 1) // '123,456,789.5'
 * toFormat('0.000000000001') // '0.00000001'
 * toFormat('0.00012345') // '0.00012'
 * toFormat('0.12345') // '0.12'
 */
function toFormat(s1, dp?: number, rm = 1, format?: any) {
  if (s1 == 0) return dp === 0 ? '0' : '0.00';
  if (s1 < 0.00000001) {
    return '< 0.00000001';
  }
  if (s1 < 1) {
    const fl = String(s1).split('.')[1];
    const flArr = [];
    for (let i = 0, k = 0; i < fl.length; i++) {
      const c = fl[i];
      if (k === 1) {
        if (+c != 0) {
          flArr.push(c);
        }
        break;
      }
      if (+c != 0 && k === 0) {
        flArr.push(c);
        k = 1;
      }
      if (+c === 0) {
        flArr.push(c);
      }
    }
    return `0.${flArr.join('')}`;
  }
  return handleReturn(BN(`${s1}`)['toFormat' as any](dp, rm, format));
}

/**
 * 取大的值
 *
 * @param {...str} str 需要取大值的字符串
 * max(['10', '13']) // 13
 * max(['10', '3']) // 10
 * max(['dsaf', 1]) // 0
 *
 */
function max(str) {
  return handleReturn(BN.max(...str));
}

/**
 * 取小的值
 *
 * @param {...str} str 需要取小值的字符串
 * max(['10', '13']) // 10
 * max(['10', '3']) // 3
 * max(['dsaf', 1]) // 0
 *
 */
function min(str) {
  return handleReturn(BN.min(...str));
}

/**
 * 生成固定个数的数字
 *
 * @param {number} len 生成数字的长度
 * @param {number} fill 要生成的数字
 */
function generateNumber(len = 9, fill = 9) {
  return new Array(len).fill(fill).join('');
}

/**
 *
 * 前补0
 *
 * @param {number|string} num 需要补0的数字
 * @param {number} n 总位数，不够在前面补0
 * @return {string} 补0后的字符串
 */
function prefixZero(num, n) {
  return (Array(n).join('0') + num).slice(-n);
  // return String(num).padStart(n, '0')
}

/**
 * 格式化精度，默认值为 `0`
 *
 * @param {string} value 后台配置的精度原始字段，如：0.0001 、0.1 、0
 * @return {number}
 */
function formatPrecision(value) {
  if (typeof value !== 'string' || value.indexOf('.') === -1) return 0;
  const split = value.split('.');
  return split[1] ? split[1].length : 0;
}

/**
 * 格式化数字
 */
function toNumber(value) {
  return Number(value) || 0;
}

function safeToFormat(s1, dp?: number, rm = 1, format?: any) {
  if (s1 == null || s1 == '-1') {
    return '--';
  }
  return handleReturn(BN(`${s1}`)['toFormat' as any](dp, rm, format));
}

function safeToFormat1(s1, dp?: number, rm = 1, format?: any) {
  if (s1 == null || s1 == '-1') {
    return '--';
  }
  if (gt(0.01, s1) && dp == 2) {
    return '<0.01';
  }
  return handleReturn(BN(`${s1}`)['toFormat' as any](dp, rm, format));
}

function formatTrend(num: number) {
  if (num > 0) {
    return `+${safeToFormat(times(num, 100))}%`;
  }
  return `-${safeToFormat(times(num, 100))}%`;
}

function getTrendNum(num: number | string, prefix = 2) {
  const numStr = `${num}`;
  if (
    !numStr.includes('.') ||
    numStr.lastIndexOf('.') + prefix >= numStr.length
  ) {
    return safeToFormat(numStr, prefix);
  }
  const dotIdx = numStr.indexOf('.');
  const dotNum = numStr.slice(dotIdx);
  const rNum = /[1-9]/;
  const dotNumIdx = rNum.exec(dotNum)?.index;
  // console.log('getTrendNum:', num, dotNum, dotIdx, dotNumIdx);
  if (dotNumIdx == null) {
    return safeToFormat(numStr.slice(0, dotIdx + prefix));
  }
  return safeToFormat(numStr.slice(0, dotIdx + dotNumIdx + prefix));
}

function formatTrend1(num: number | string) {
  if (num == '-1' || num == null) {
    return '--';
  }
  if (gt(num, 0)) {
    return `+${getTrendNum(num)}%`;
  }
  const newNum = getTrendNum(num);
  return `${newNum}%`;
}

function formatTrend2(num: number | string) {
  if (num == '-1' || num == null) {
    return '--';
  }
  if (gt(num, 0)) {
    return `+${safeToFormat(num, 2)}%`;
  }
  const newNum = safeToFormat(num, 2);
  return `${newNum}%`;
}

function maskDigit(num: string, visible: boolean) {
  const displayNum = num == null || num === '' ? '--' : num;
  return visible ? displayNum : `${new Array(4).fill('*').join('')}`;
}

// 去除小数结尾的0
function removeFloatZero(value: number | string) {
  return Number(value)
    .toString()
    .replace(/(\.[0-9]*[1-9])0+$/, '$1');
}

const formatKMB = (num?: string | number, fixScale = 2) => {
  if (num == null || num === '' || num === '-1') {
    return '--';
  }
  if (lt(num, 1000)) {
    return safeToFormat(num, 0);
  } else if (lt(num, 1000000)) {
    const output = safeToFormat(div(num, 1000), fixScale);
    return `${output}K`;
  } else if (lt(num, 1000000000)) {
    const output = safeToFormat(div(num, 1000000), fixScale);
    return `${output}M`;
  } else {
    const output = safeToFormat(div(num, 1000000000), fixScale);
    return `${output}B`;
  }
};

type FormatPercentageOption = {
  showPlus?: boolean;
  friendly1?: boolean;
  scale?: number;
  friendly1WithSymbol?: boolean;
  decimal?: number;
};
export function formatPercentage(
  number: number | string,
  {
    showPlus = true,
    friendly1 = true,
    friendly1WithSymbol = true,
    scale = 100,
    decimal = 2,
  }: FormatPercentageOption = {}
): string {
  const positiveSymbol = '+';
  let value: number;
  if (typeof number === 'string') {
    value = parseFloat(number) * scale || 0;
  } else {
    value = number * scale;
  }
  const precisionMinNum = 1 / Math.pow(10, decimal);
  const absValue = Math.abs(value);
  // 整数百分比，小于1%，展示为1%，友好化展示
  const numStr = removeTrailingZeros(
    friendly1 && absValue < precisionMinNum && absValue > 0
      ? `${value < 0 ? '-' : ''}${friendly1WithSymbol ? '<' : ''}${String(precisionMinNum)}`
      : BigNumber(value).toFormat(decimal)
  );
  if (value > 0) {
    return `${showPlus ? positiveSymbol : ''}${numStr}%`;
  } else if (value === 0) {
    return `0%`;
  } else {
    return `${numStr}%`;
  }
}

export const formatNumberPercentage = (
  number: string | number,
  decimal = 2,
  opt: FormatPercentageOption = {}
) => {
  return formatPercentage(number, { decimal, ...opt });
};

export function removeTrailingZeros(numStr: string): string {
  return numStr.replace(/(\.[0-9]*[1-9])0*|(\.0*)$/, '$1');
}

export function getInputNumber(
  value: string,
  {
    intBit = 7,
    decimalBit = 4,
    allowNegative = false,
    nanReset = true,
    resetToZero = false,
  }: {
    intBit?: number;
    decimalBit?: number;
    allowNegative?: boolean;
    // 值为NaN时，是否重置为
    nanReset?: boolean;
    resetToZero?: boolean;
  } = {}
): string {
  const reset = resetToZero ? '0' : '';
  // 非数字字符直接重置
  const numChars = allowNegative ? /^[.\-0-9]+$/ : /^[.0-9]+$/;
  if (!numChars.test(value)) {
    return reset;
  }
  const num = Number(value);
  if (isNaN(num)) {
    return nanReset ? reset : value;
  }
  const [int, decimal] = value.split('.');
  const intStr = int.slice(0, int.startsWith('-') ? intBit + 1 : intBit);
  if (decimalBit === 0 || decimal === undefined) {
    return intStr;
  }
  const decimalStr = decimal.slice(0, decimalBit);
  return `${intStr}.${decimalStr}`;
}

// 一个全天候的数字格式化工具函数
export function formatNumber(
  number?: number | string,
  {
    decimal = 2,
    kmb = true,
    kmbDecimal = decimal,
    maxKmbUnit = 'B',
    minKmbUnit = 'K',
    smallNumberUseValidDigits = true,
    smallNumberValidDigits = 4,
    negativeZero = false,
    collapseZero = true,
    collapseZeroAmount = 5,
    smallerThanX,
    noTrailZero = false,
    roundMode,
    zeroAsNone = false,
    scale,
    showPlus = false,
    suffix = '',
    prefix = '',
    placeholder = '--',
    sntWhenBiggerThanEx = 16,
    sngDecimal = 2,
  }: {
    // 小数位数
    decimal?: number;
    // 小数是否使用有效位来取值
    smallNumberUseValidDigits?: boolean;
    // 比较小的数（绝对值小于1）的有效位
    smallNumberValidDigits?: number;
    // 是否使用KMB展示
    kmb?: boolean;
    // 最大的KMB 单位
    maxKmbUnit?: 'K' | 'M' | 'B' | 'T';
    // 最小 KMB 单位
    minKmbUnit?: 'K' | 'M' | 'B' | 'T';
    kmbDecimal?: number;

    // 是否允许 -0，例如 0:00：默认false
    negativeZero?: boolean;

    // 是否折叠0，例如0.0000006 折叠为：0.{6}6
    collapseZero?: boolean;
    // 折叠 0 的数量，超过数量的0才会被折叠，默认：5
    collapseZeroAmount?: number;
    // 是否设置小于 x 的值展示为 < 0.00001;，这里需要传入的是x的小数位数
    smallerThanX?: number;
    // 不要数字末尾的0，例如将 0.00变成 0, 默认：false
    noTrailZero?: boolean;
    // 0 当做无效数字，默认false
    zeroAsNone?: boolean;
    // bignumber 的“四舍五入”逻辑，参考bignumber 文档，默认值跟随bignumber全局设置
    roundMode?: BigNumber.RoundingMode;
    // 給原始值 * 一个数，例如：可以用于百分比时 * 100，默认无
    scale?: number;
    // 正数是否显示 + 号，默认：false
    showPlus?: boolean;
    // 前缀，例如$ ¥，默认：空
    prefix?: string;
    // 后缀：例如 %，默认：空
    suffix?: string;
    // 无效数字的占位符，默认：'--'
    placeholder?: string;

    // 超过 10^x 的值使用科学计数法
    sntWhenBiggerThanEx?: number;
    sngDecimal?: number;
  } = {}
): string {
  let num = BigNumber(number);
  if (scale !== undefined) {
    num = num.times(scale);
  }
  if (showPlus && num.gt(0)) {
    prefix = `${prefix}+`;
  }
  const addAddon = (s: string) => `${prefix}${s}${suffix}`;
  if (num.isNaN() || !num.isFinite() || (zeroAsNone && num.eq(0))) {
    return addAddon(placeholder);
  }

  const sntBigNumber = BigNumber(10).shiftedBy(sntWhenBiggerThanEx);
  if (num.gte(sntBigNumber)) {
    // 超大数用科学计数法展示
    return addAddon(num.toExponential(sngDecimal).replace('e+', 'x10^'));
  }

  let numStr: string;
  let unit: string;
  let isKmb = false;

  if (num.abs().lt(1)) {
    // 小于1的数
    if (smallNumberUseValidDigits) {
      numStr = num.toPrecision(smallNumberValidDigits, roundMode);
    } else {
      numStr = num.toFixed(decimal, roundMode);

      // 如果不允许 负0的出现，移除 负号
      if (!negativeZero && BigNumber(numStr).eq(0) && numStr.startsWith('-')) {
        numStr = numStr.substring(1, numStr.length);
      }
    }
    if (noTrailZero) {
      numStr = removeTrailingZeros(numStr);
    }
    if (smallerThanX !== undefined) {
      const smallNumber = BigNumber(1).shiftedBy(-smallerThanX);
      if (num.abs().lt(smallNumber)) {
        numStr = `${num.lt(0) ? '-' : ''}<${smallNumber.toFixed()}`;
      }
    }
    if (collapseZero && collapseZeroAmount > 3) {
      numStr = numStr.replace(
        new RegExp(`0{${collapseZeroAmount},}`, 'g'),
        (p) => {
          return `{${p.length}}`;
        }
      );
    }
  } else {
    if (kmb) {
      const kmbUnits = 'KMBT';
      const minIndex = Math.max(0, kmbUnits.indexOf(minKmbUnit));
      const startIndex = kmbUnits.indexOf(maxKmbUnit);
      for (let i = startIndex; i >= minIndex; i--) {
        const nNum = num.shiftedBy(-((i + 1) * 3));
        if (nNum.gte(1)) {
          isKmb = true;
          unit = kmbUnits[i];
          num = nNum;
          break;
        }
      }
    }
    numStr = num.toFormat(isKmb ? kmbDecimal : decimal, roundMode);
  }
  return addAddon(`${numStr}${unit || ''}`);
}

export {
  plus,
  minus,
  times,
  div,
  idiv,
  sd,
  gt,
  gte,
  lt,
  lte,
  eq,
  toFixed,
  toFormat,
  sum,
  random,
  abs,
  max,
  min,
  generateNumber,
  prefixZero,
  formatPrecision,
  toNumber,
  safeToFormat,
  safeToFormat1,
  maskDigit,
  formatTrend,
  formatTrend1,
  formatTrend2,
  getTrendNum,
  formatKMB,
  removeFloatZero,
};
